import { useEffect, useState, ReactNode } from 'react';
import { useShellRouter } from '@/hooks/useShellRouter';
import { processAuthTokenFromUrl, cleanAuthTokenFromUrl, storeAuthToken, getAuthToken } from '../utils/authToken';
import { hasHandoverToken, isTenantLabel } from '../utils/handoverParams';
import { posthog } from '../lib/posthog';
import { useSession } from '../context/SessionContext';
import { useSignInTarget, useSignInUrl } from '../hooks/useSignInUrl';
import { savePendingDeepLink, clearPendingDeepLink } from '../utils/pendingRoute';
import StatusCard from './StatusCard';
import { config } from '@/config/runtime';
import AppLoader from '@/components/AppLoader';

interface AuthGuardProps {
  children: ReactNode;
}

// Per-tab marker recording that this tab already tried the automatic hop to sign-in.
// sessionStorage, not localStorage: the guard should not leak across tabs, and it should
// clear itself when the tab closes.
const LOGIN_REDIRECT_KEY = 'authGuardLoginRedirect';

// Set by middleware.ts when a handover credential arrived and was refused. See there for
// why it exists; here it is read once and deleted.
const HANDOVER_REJECTED_COOKIE = 'handoverRejected';

const readCookie = (name: string): string | undefined =>
  document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${name}=`))
    ?.split('=')[1];

/**
 * Authentication is decided by the SERVER (/api/me via SessionContext), not by decoding a
 * token in the browser.
 *
 * That is required because in deployed environments the credential is an HttpOnly cookie
 * which JS cannot read — the old check (`!!getAuthToken()`) would see null and show
 * "Secure Access Required" to a perfectly logged-in user. Asking the server works in BOTH
 * modes: localhost sends the Bearer header, deployed sends the cookie.
 */
export default function AuthGuard({ children }: AuthGuardProps) {
  const { status, user, tenant, refresh } = useSession();
  const router = useShellRouter();
  // Holds the spinner while the browser navigates away, so the sign-in card never flashes
  // up for a moment before the redirect takes effect.
  const [redirectingToLogin, setRedirectingToLogin] = useState(false);
  // True while a credential picked up from the URL or the handover cookie is still being
  // evaluated by the server. SessionProvider fires its OWN /api/me on mount, independently
  // of the handover below, and both components are dynamically imported so their order is
  // not guaranteed. If that first call 401s before the token is stored, status flips to
  // 'unauthenticated' for a moment — and the automatic redirect would fire on exactly the
  // hop that is returning FROM login. Suppress it until the handover has an answer.
  const [handoverInFlight, setHandoverInFlight] = useState(false);
  // The automatic hop to sign-in already ran in this tab and we are STILL signed out.
  // That rules out the cheap explanation (a missing cookie the login app could fix by
  // forwarding us straight back) and means the login app forwarded us on with a
  // credential this host rejects. The manual button then has to force the form.
  const [autoSignInFailed, setAutoSignInFailed] = useState(false);

  // Entry path for a token handed over in the URL (?authToken=… / ?userToken=…). Persist
  // it, clean the URL, then re-ask the server. Harmless in cookie mode (no such param).
  //
  // Normally middleware.ts has already moved it into the handover cookie and redirected,
  // so this branch does not fire. It still has to work: the middleware matcher excludes
  // static paths, and a Tauri/webview load can reach the page without an Edge hop.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);

    // Mobile/Tauri tenant hint. Validated before it is stored: it is later interpolated
    // into the sign-in host, and it arrives from a link anyone can craft.
    const nodeParam = urlParams.get('node');
    if (isTenantLabel(nodeParam)) localStorage.setItem('node', nodeParam);

    // The main app just handed us a dead credential. That is positive evidence its own
    // session is stale — not merely that this visitor has none — so the polite sign-in
    // link is known in advance to be futile: the login page would check its raw
    // isLoggedIn flag, never show the form, and forward the same dead token back here.
    // Spend the attempt now so the very first hop is the ?logout=true one that clears
    // the stale flag. Without this the user watches a full bounce first.
    const rejected = readCookie(HANDOVER_REJECTED_COOKIE);
    if (rejected) {
      document.cookie = `${HANDOVER_REJECTED_COOKIE}=; path=/; max-age=0`;

      // The tenant the refused token named, when it named one. On the shared microapps
      // host this is the only thing that lets a sign-in link be built for a first-time
      // visitor: there is no ?node= on the SPA's redirect and nothing in localStorage yet.
      if (isTenantLabel(rejected)) localStorage.setItem('node', rejected);

      try {
        sessionStorage.setItem(LOGIN_REDIRECT_KEY, '1');
      } catch {
        // No sessionStorage: the escalation is lost, the plain link still works.
      }
    }

    if (hasHandoverToken(urlParams)) {
      // Strip it whether or not storing succeeded. The param is a JWT; leaving it in the
      // address bar puts it into browser history, referrer headers and any screenshot the
      // user sends us. Previously a failed store left it on screen indefinitely.
      try {
        processAuthTokenFromUrl(window.location.search);
      } finally {
        window.history.replaceState({}, '', cleanAuthTokenFromUrl());
      }
      setHandoverInFlight(true);
      void refresh().finally(() => setHandoverInFlight(false));
      return;
    }

    // Handover cookie (survives when query params don't). storeAuthToken() mirrors it
    // into localStorage AND refreshes the cookie's max-age.
    //
    // Do NOT clear the cookie here. This is now the PRIMARY hand-off path, not a
    // fallback: middleware.ts moves ?authToken= into this cookie and redirects, so on
    // the hop back from sign-in the query param is already gone and this branch is what
    // picks the credential up and mirrors it into localStorage. Clearing it destroyed the
    // one credential getServerSideProps can read (see the comment below), which made
    // /view/[id] and /casefiles alternate between working and "Tenant not found" on
    // every other refresh: load N resolved the tenant then deleted the cookie, load
    // N+1 had nothing to resolve from and re-wrote it.
    const cookieToken = readCookie('authToken');
    if (cookieToken) {
      storeAuthToken(cookieToken);
      setHandoverInFlight(true);
      void refresh().finally(() => setHandoverInFlight(false));
      return;
    }

    // Keep the server-side cookie in step with localStorage (used by getServerSideProps).
    const existing = getAuthToken();
    if (existing) storeAuthToken(existing);
  }, [refresh]);

  // The effect above runs once, on mount. A client-side navigation that carries
  // a handover token is never seen by it, and middleware cannot help either — a Next route
  // change is history.pushState, not a request — so the JWT would sit in the address bar
  // for the rest of the session. That is exactly what a sidebar click used to do.
  //
  // Nothing in the shell appends the token to an internal href any more, so this is a net
  // rather than a load-bearing path. It captures BEFORE it strips, so it can never be the
  // thing that discards the only copy of a credential.
  useEffect(() => {
    const captureAndStrip = () => {
      if (!hasHandoverToken(new URLSearchParams(window.location.search))) return;
      try {
        processAuthTokenFromUrl(window.location.search);
      } finally {
        window.history.replaceState({}, '', cleanAuthTokenFromUrl());
      }
    };

    router.events.on('routeChangeComplete', captureAndStrip);
    return () => router.events.off('routeChangeComplete', captureAndStrip);
  }, [router.events]);

  // Re-check with the server when another tab signs in/out.
  useEffect(() => {
    const onStorage = () => void refresh();
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);

  useEffect(() => {
    // `user.id` is part of the guard, not just the payload: posthog.identify() takes the
    // distinct id as a required string, and calling it with undefined starts an
    // identified session with no subject — every later event attaches to nothing. The
    // Next build's ignored type errors hid this.
    if (status !== 'authenticated' || !user?.id || !posthog.__loaded) return;

    const maskPII = config.posthogMaskPII;
    posthog.identify(
      user.id,
      maskPII
        ? { role: user.role, tenant }
        : { email: user.email, name: user.fullName, role: user.role, tenant }
    );
    if (tenant) {
      posthog.group('tenant', tenant, maskPII ? undefined : { name: tenant });
    }
  }, [status, user, tenant]);

  useEffect(() => {
    if (status === 'unauthenticated' && posthog.__loaded) {
      posthog.reset();
    }
  }, [status]);

  /**
   * Record the tenant as `node` whenever a session names one.
   *
   * The two writes above only fire for a visitor who arrived with ?node= or whose handover
   * was refused. Neither covers the `onlyMicroapps` cohort, and they are the ones who need
   * it: the SPA sends them here as `https://microapps.<base>?userToken=…` with no ?node= at
   * all (see utils/handoverParams), so on the shared host nothing durable ever names their
   * tenant. Their session knows it — this is simply writing down what /api/me already said.
   *
   * It matters once the session is gone. resolveNode still has the `tenant` cookie and the
   * stored token to fall back on, but both expire, and when they do the sign-in link cannot
   * be built at all on a hostname that names no tenant. `node` is the one hint meant to
   * outlive the credential.
   */
  useEffect(() => {
    if (status !== 'authenticated' || !isTenantLabel(tenant)) return;
    try {
      if (localStorage.getItem('node') !== tenant) localStorage.setItem('node', tenant);
    } catch {
      // Private mode or a full quota. Nothing to do — the other hints still apply.
    }
  }, [status, tenant]);

  const isAuthenticated = status === 'authenticated';

  // login.html is served by the MAIN app's distribution, not by this micro-apps host —
  // they are separate origins, so the old same-origin link pointed at a path that does
  // not exist here and the button appeared to do nothing. That is the failure anyone
  // gets opening an emailed micro-apps link in a fresh tab.
  //
  // On the shared microapps host with no tenant hint this falls back to
  // NEXT_PUBLIC_LOGIN_ORIGIN, then to synergy (see siyaDomain). Null only when the probe
  // cannot reach any candidate, in which case no button renders.
  //
  // Both variants are resolved up front because hooks cannot be called from the render
  // branch that picks between them. They share one probe, so the second costs nothing.
  const { url: signInHref, probeSettled: signInProbeSettled } = useSignInTarget(tenant);
  const forceSignInHref = useSignInUrl(tenant, true);
  const awaitingSignInProbe =
    !signInHref && !autoSignInFailed && !signInProbeSettled;

  const authSpinner = (
    <AppLoader />
  );

  // Remember the page the user was trying to reach before sending them anywhere. The
  // sign-in link carries it as returnUrl too, but that is dropped on the ?logout=true
  // path and for anyone whose post-login landing is chat — see utils/pendingRoute.
  useEffect(() => {
    if (status === 'unauthenticated') savePendingDeepLink();
  }, [status]);

  // Arrived on a real page with a working session: the deep link did its job (or was
  // never needed), so it must not linger and hijack a later visit to the root.
  useEffect(() => {
    if (status !== 'authenticated') return;
    if (window.location.pathname !== '/') clearPendingDeepLink();
  }, [status]);

  // With no session at all, go straight to sign-in rather than making the user click
  // through a card first. Two conditions keep that safe.
  useEffect(() => {
    if (status !== 'unauthenticated' || !signInHref) return;
    if (handoverInFlight) return;

    // Loop guard, and it is checked FIRST — before the framed bail-out below — because
    // it is what escalates the card's button to ?logout=true. It used to sit after that
    // bail-out, so inside the chat's Micro Apps panel (how most people open micro-apps)
    // it never ran: the card kept offering the plain link, the login page kept forwarding
    // the user straight back on their stale isLoggedIn flag, and every click repeated the
    // same bounce. Reaching this point with the marker already set means an attempt has
    // been made in this tab and we are STILL signed out, whoever started it.
    let attempted = false;
    try {
      attempted = sessionStorage.getItem(LOGIN_REDIRECT_KEY) !== null;
    } catch {
      // No sessionStorage means no loop guard, so don't start a loop we cannot stop.
      return;
    }

    if (attempted) {
      setAutoSignInFailed(true);
      return;
    }

    let framed = false;
    try {
      framed = window.self !== window.top;
    } catch {
      framed = true;
    }
    // A cross-origin iframe cannot navigate the top window without a user gesture, so an
    // automatic redirect here would load login INTO the chat's Micro Apps panel. The card
    // stays, and its link carries both target="_top" and the click that authorises it.
    // Deliberately NOT marked as attempted: nothing was attempted, and marking here would
    // force ?logout=true on the user's very first click and destroy a main-app session
    // that may be perfectly good.
    if (framed) return;

    try {
      sessionStorage.setItem(LOGIN_REDIRECT_KEY, '1');
    } catch {
      return;
    }

    setRedirectingToLogin(true);
    window.location.href = signInHref;
  }, [status, signInHref, handoverInFlight]);

  // A completed sign-in re-arms the automatic redirect for this tab.
  useEffect(() => {
    if (status !== 'authenticated') return;
    setAutoSignInFailed(false);
    try {
      sessionStorage.removeItem(LOGIN_REDIRECT_KEY);
    } catch {}
  }, [status]);

  // NEVER decide while /api/me is in flight — doing so bounces a signed-in user to login
  // on every refresh.
  if (status === 'loading' || redirectingToLogin || handoverInFlight) {
    return authSpinner;
  }

  // We could not reach /api/me. That is not proof the user is signed out, so offer a way
  // back in rather than the bare login screen — and never a retry, which just repeats the
  // same failing request.
  if (status === 'error') {
    return (
      <StatusCard
        title="Couldn't Verify Your Session"
        message={<>We couldn&apos;t confirm you&apos;re signed in.<br />You have not been signed out.</>}
        note={{ title: 'Need Help?', body: 'If signing in again doesn\u2019t help, contact SIYA Support.' }}
        actions={
          signInHref ? [{ label: 'Login with SIYA', href: signInHref }] : []
        }
      />
    );
  }

  if (!isAuthenticated) {
    // Spinner while the login-path probe runs or until the automatic redirect fires.
    if (!autoSignInFailed && (signInHref || awaitingSignInProbe)) {
      return authSpinner;
    }

    const cardSignInHref = autoSignInFailed ? forceSignInHref : signInHref;

    return (
      <StatusCard
        title="Secure Access Required"
        message={<>You need valid credentials to access the SIYA Dashboard.<br />Please contact your system administrator</>}
        note={{ title: 'Need Help?', body: 'Contact IT Support for dashboard access credentials.' }}
        actions={
          cardSignInHref
            ? [{
                label: 'Login with SIYA',
                href: cardSignInHref,
                onClick: () => {
                  try {
                    sessionStorage.setItem(LOGIN_REDIRECT_KEY, '1');
                  } catch {
                    // Nothing to do — the link still navigates, we just cannot escalate.
                  }
                },
              }]
            : []
        }
      />
    );
  }

  return <>{children}</>;
}
